<script lang="ts" setup name="OrganisationPortCallsDetailsIndex">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useRouteBack } from '~/features/useRouteBack';
import { useService } from '~/features/useService.js';
import { isEmpty } from '~/features/useUtilities';

import Service from '~/services/Service';

import BaseCard from '~/components/BaseCard.vue';
import BaseCardLink from '~/components/BaseCardLink.vue';
import BasePage from '~/components/BasePage.vue';
import ContactCard from '~/components/ContactCard.vue';
import FilterMenuSelectSingle from '~/components/FilterMenuSelectSingle.vue';
import OrganisationShipmentsShowHeading from '~/components/OrganisationShipmentsShowHeading.vue';
import OrganisationShipmentVesselTracking from '~/components/OrganisationShipmentVesselTracking.vue';
import RouterTabs from '~/components/RouterTabs.vue';
import ServiceCard from '~/components/ServiceCard.vue';
import TextToggle from '~/components/TextToggle.vue';

import type {
  IPortCallsDetailsHeader,
  IPortCallsDetailsPortSelection,
  IPortCallsDetailsSelectionResponse,
  IPortCallsMasterContacts,
  IPortCallsTrackingResponse,
  IPortCallsVesselLinkData,
  IRouterTabLink,
  IVesselVoyageData,
} from '~/types';

const { organisationId, portId, voyageId } = defineProps<{
  organisationId: OrganisationId;
  voyageId: string;
  portId: string;
}>();

const router = useRouter();
const route = useRoute();

const vesselMapData = ref<IVesselVoyageData>();
const isMasterContactsVisible = ref(false);
const isPageHeaderLoading = ref(true);
const masterContactsWidget = ref<IPortCallsMasterContacts[]>([]);
const currentPort = ref<{
  id: number;
  name: string;
  lastVoyagePortId: string;
}>({
  id: 0,
  name: '',
  lastVoyagePortId: portId,
});
const portOptions = ref<IPortCallsDetailsPortSelection[]>([]);
const serviceState = ref();

const vessel = reactive<IPortCallsVesselLinkData>({
  id: '',
  name: '',
});

const header = reactive<IPortCallsDetailsHeader>({
  portName: '',
  vesselName: '',
});

const fullPortCallParameters = {
  organisationId: organisationId,
  voyageId: voyageId,
  portId: portId,
};

const portCallsIndexRoute = useRouteBack({
  name: 'OrganisationPortCallsIndex',
});

const routeKey = computed(
  () => `${String(route.name)}-${Object.values(route.params).join('-')}`,
);

const vesselLink = computed(() =>
  vessel.id
    ? router.resolve({
        name: 'OrganisationVesselsShow',
        params: {
          id: vessel.id,
          name: vessel.name,
        },
      }).href
    : null,
);

const portOptionsNames = computed(() =>
  portOptions.value.map((port) => port.portName),
);

const mappedPortOptions = computed(() => {
  return portOptions.value.map((port) => ({
    id: port.portId,
    name: port.portName,
  }));
});

const shouldShowPortToggle = computed(() => {
  return portOptionsNames.value.length > 1 && portOptionsNames.value.length < 4;
});

const toPath = (name: string) => {
  return {
    name,
    params: fullPortCallParameters,
  };
};

const tabs: IRouterTabLink[] = [
  {
    label: 'Port Overview',
    icon: 'mdi:information',
    to: toPath('OrganisationPortCallsDetailsOverview'),
  },
  {
    label: 'Cargo Line-up',
    icon: 'mdi:package-variant-closed',
    to: toPath('OrganisationPortCallsDetailsCargo'),
  },
  {
    label: 'Port rotation',
    icon: 'mdi:map-marker',
    to: toPath('OrganisationPortCallsDetailsRotation'),
  },
  {
    label: 'Documents',
    icon: 'material-symbols:contract',
    to: toPath('OrganisationPortCallsDetailsDocuments'),
  },
  {
    label: 'Contacts',
    icon: 'ic:outline-contact-page',
    to: toPath('OrganisationPortCallsDetailsContacts'),
  },
];

const vesselTrackingServiceState = useService(
  Service.organisation(organisationId)
    .portCallsCalls(voyageId)
    .tracking(portId),
  ({ data }: IPortCallsTrackingResponse) => {
    if (isEmpty(data)) {
      return false;
    }

    const { location, masterContacts, vesselLink } = data;

    if (location) {
      vesselMapData.value = location;
    }

    if (masterContacts) {
      masterContactsWidget.value = masterContacts;
      isMasterContactsVisible.value = masterContacts.length > 0;
    }

    if (vesselLink) {
      vessel.id = vesselLink.id ?? '';
      vessel.name = vesselLink.name ?? '';
    }

    return true;
  },
);

const fetchPortSelection = () => {
  serviceState.value = useService(
    Service.organisation(organisationId)
      .portCallsCalls(voyageId)
      .selection(currentPort.value.lastVoyagePortId),
    ({ data }: IPortCallsDetailsSelectionResponse) => {
      if (isEmpty(data)) return false;
      header.portName = data.header?.portName ?? '';
      header.vesselName = data.header?.vesselName ?? '';
      isPageHeaderLoading.value = false;
      return true;
    },
  );
};

const fetchInitialPortSelection = () => {
  Service.organisation(organisationId)
    .portCallsCalls(voyageId)
    .selection(portId)
    .onSuccess(({ data }: IPortCallsDetailsSelectionResponse) => {
      const initialPort = data.selection.find((port) => port.isCurrent);

      if (initialPort) {
        currentPort.value = {
          id: initialPort.portId,
          name: initialPort.portName,
          lastVoyagePortId: initialPort.lastVoyagePortId,
        };

        header.portName = data.header?.portName ?? '';
        header.vesselName = data.header?.vesselName ?? '';

        portOptions.value = data.selection;
        isPageHeaderLoading.value = false;
      }
    })
    .execute();
};

const setCurrentPort = (selectedName: string) => {
  const selectedPort = portOptions.value.find(
    (port) => port.portName === selectedName,
  );

  if (selectedPort && selectedPort.portId !== currentPort.value.id) {
    currentPort.value = {
      id: selectedPort.portId,
      name: selectedPort.portName,
      lastVoyagePortId: selectedPort.lastVoyagePortId,
    };

    isPageHeaderLoading.value = true;

    const newUrl = router.resolve({
      name: route.name!,
      params: {
        ...route.params,
        portId: selectedPort.lastVoyagePortId,
      },
      query: route.query,
      hash: route.hash,
    }).href;

    window.history.replaceState({ ...window.history.state }, '', newUrl);
  }
};

watch(() => currentPort.value.lastVoyagePortId, fetchPortSelection);

onMounted(() => {
  fetchInitialPortSelection();
  fetchPortSelection();
});
</script>

<template>
  <BasePage
    :backButton="{
      label: 'Port calls',
      to: portCallsIndexRoute,
    }"
    withAside
  >
    <template #heading>
      <OrganisationShipmentsShowHeading
        :loading="isPageHeaderLoading"
        :bookingNo="header.portName"
        :tradeLane="header.vesselName"
      />
    </template>

    <template #button>
      <div class="flex items-center gap-3">
        <div v-if="portOptionsNames.length > 1" class="text-charcoal-6">
          Port call:
        </div>

        <TextToggle
          v-if="portOptionsNames.length && shouldShowPortToggle"
          class="bg-white"
          :options="portOptionsNames"
          :disabled="isPageHeaderLoading"
          :modelValue="currentPort.name"
          @update:modelValue="setCurrentPort"
        />

        <FilterMenuSelectSingle
          v-if="portOptionsNames.length > 3"
          :modelValue="{ id: currentPort.id, name: currentPort.name }"
          :label="currentPort.name"
          :options="mappedPortOptions"
          @update:modelValue="(value) => setCurrentPort(value.name)"
        />
      </div>
    </template>

    <div>
      <RouterTabs :tabs="tabs"> </RouterTabs>
      <div class="mt-2">
        <ServiceCard v-bind="serviceState" styled>
          <RouterView v-slot="{ Component }">
            <component
              :is="Component"
              :key="routeKey"
              :portId="currentPort.lastVoyagePortId"
            />
          </RouterView>
        </ServiceCard>
      </div>
    </div>

    <template #aside>
      <div class="space-y-2">
        <OrganisationShipmentVesselTracking
          :service-state="vesselTrackingServiceState"
          :mapData="vesselMapData"
        />

        <BaseCard
          title="Contacts"
          subtitle="Need contacts of other agents, port operators or G2 Ocean team?"
        >
          <BaseCardLink
            :to="toPath('OrganisationPortCallsDetailsContacts')"
            class="uppercase"
          >
            Go to Contacts
          </BaseCardLink>
        </BaseCard>

        <ContactCard
          v-if="isMasterContactsVisible"
          :contacts="masterContactsWidget"
          title="Master Contacts"
        />

        <BaseCard
          title="Vessel Documents"
          subtitle="Need access to vessel certificates, drawings and other documents?"
        >
          <BaseCardLink v-if="vesselLink" :href="vesselLink" class="uppercase">
            Go to vessel documents
          </BaseCardLink>
        </BaseCard>
      </div>
    </template>
  </BasePage>
</template>
